'use client';

import React, { useEffect, useState } from 'react';
import Service from '@/service';
import Review from '@/components/organisms/review';
import useCart from '@/lib/hooks/useCart';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import getCookieValue from '@/utils/cookie';
import { getCurrencyByLocale } from '@/utils/currency-helpers';
import { CheckoutDetails } from '@shared/types/cart/Checkout'
import { useRouter } from 'next/navigation';
import toast from '@/components/atoms/toaster/helpers/toast';
import Popup from '@/components/atoms/project-builder/popup';
import Icon from '@/components/icons/x-mark';
import Image from "@/components/atoms/Image";
import Typography from '@/components/atoms/typography';
import Button from '@/components/atoms/button';
import { useCartConfig } from '@/providers/cart-config/CartConfigContext';


const ReviewTastic = ({ data }: any) => {
  const [loading, setLoading] = useState(false)
  const [checkoutDetails, setCheckoutDetails] = useState<CheckoutDetails>({
    PO_Number: "",
    Account_Number: "",
    Account_Name: "",
    Business_Name: "",
    Address: "",
    Suburb: "",
    Postcode: "",
    State: "",
    Country: "",
    ShippingMethodName: "",
    DeliveryInstructions: "",
    First_Name: "",
    Last_Name: "",
    Phone: "",
    City: "",
    ctOrderNumber: "",
    brochureOrderNumber: "",
  });
  const { translate } = useTranslation();
  const router = useRouter()
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { cart, shippingMethods } = useCart(
    selectedBusinessUnit?.key,
    selectedStore?.key,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const locale = getCookieValue('locale');
  const currencyCode = getCurrencyByLocale(locale);


  const fetchCheckoutDetails = async () => {
    setLoading(true);
    try {
      const response = await Service.checkout.details({ currency: currencyCode, businessUnitKey: selectedBusinessUnit?.key as string });
      console.log('[checkout-details] full response:', response?.data);
      console.log('[checkout-details] ctOrderNumber:', response?.data?.ctOrderNumber);
      console.log('[checkout-details] brochureOrderNumber:', response?.data?.brochureOrderNumber);
      if (response?.data) {
        const ctOrderNumber = response?.data?.ctOrderNumber ?? "";
        const brochureOrderNumber = response?.data?.brochureOrderNumber ?? "";

        // Only save to sessionStorage if we have a non-empty value AND session
        // doesn't already have one — prevents overwriting the original value
        // on page reload after eWay redirect.
        if (ctOrderNumber && !sessionStorage.getItem('checkout_ctOrderNumber')) {
          sessionStorage.setItem('checkout_ctOrderNumber', ctOrderNumber);
          console.log('[checkout-details] stored ctOrderNumber in session:', ctOrderNumber);
        } else {
          console.log('[checkout-details] skipped session write — already set to:', sessionStorage.getItem('checkout_ctOrderNumber'));
        }
        if (brochureOrderNumber && !sessionStorage.getItem('checkout_brochureOrderNumber')) {
          sessionStorage.setItem('checkout_brochureOrderNumber', brochureOrderNumber);
        }

        const checkoutData: any = {
          PO_Number: response?.data?.poNumber ?? "",
          Account_Number: response?.data?.accountNumber ?? "",
          Account_Name: response?.data?.accountName ?? "",
          Business_Name: response?.data?.accountName ?? "",
          Address: response?.data?.shippingAddress?.streetName ?? "",
          Suburb: response?.data?.shippingAddress?.suburb ?? "",
          Postcode: response?.data?.shippingAddress?.postcode ?? "",
          State: response?.data?.shippingAddress?.state ?? "",
          Country: response?.data?.shippingAddress?.country ?? "",
          ShippingMethodName: response?.data?.shippingMethodName ?? "",
          DeliveryInstructions: response?.data?.deliveryInstructions ?? "",
          First_Name: response?.data?.shippingAddress?.firstName ?? "",
          Last_Name: response?.data?.shippingAddress?.lastName ?? "",
          Phone: response?.data?.shippingAddress?.phone ?? "",
          City: response?.data?.shippingAddress?.city ?? "",
          ctOrderNumber,
          brochureOrderNumber,
        }
        setCheckoutDetails((prev) => {
          return {
            ...prev,
            ...checkoutData
          }
        });

      } else {
        console.log('failed to fetch checkout details:: error:');
        toast.error("We're experiencing an issue. Please reach out using the details provided on this page.", { position: 'top-right' })
      }
    } catch (e) {
      console.log('failed to fetch checkout:: error:', e);
      toast.error("We're experiencing an issue. Please reach out using the details provided on this page.", { position: 'top-right' })
    } finally {
      setLoading(false)
    }
  }

  const onSubmitOrder = async (transactionId?: any) => {
    setLoading(true)
    // After an eWay redirect the page reloads and React state is cleared before fetchCheckoutDetails
    // completes, so read from sessionStorage which was set when the API first responded.
    // const ctOrderNumber = checkoutDetails.ctOrderNumber || sessionStorage.getItem('checkout_ctOrderNumber') || '';
    // const brochureOrderNumber = checkoutDetails.brochureOrderNumber || sessionStorage.getItem('checkout_brochureOrderNumber') || '';
    const ctOrderNumber = sessionStorage.getItem('checkout_ctOrderNumber') || '';
    const brochureOrderNumber = sessionStorage.getItem('checkout_brochureOrderNumber') || '';

    console.log('[onSubmitOrder] ctOrderNumber from session (used for create-order + redirect):', ctOrderNumber);
    try {
      const result = await Service.cart.createOrder({
        currency: currencyCode,
        transactionId: transactionId ? String(transactionId) : undefined,
        ctOrderNumber,
        brochureOrderNumber,
      });
      if (result?.data) {
        sessionStorage.removeItem('checkout_ctOrderNumber');
        sessionStorage.removeItem('checkout_brochureOrderNumber');
        router.push(`/order-confirmed?orderNumber=${ctOrderNumber}`);
      } else {
        setLoading(false)
        console.log('failed to create order:: error:');
        setIsModalOpen(true);
      }
    } catch (e) {
      setLoading(false)
      console.log('failed to save checkout:: error:', e);
      setIsModalOpen(true);
    }
  }


  useEffect(() => {
    if (currencyCode) {
      fetchCheckoutDetails()
    }
  }, [currencyCode])

  // Payment callback is now handled in ReviewOrderSummary component


  return (
    <>
      <Review
        handleReview={onSubmitOrder}
        loading={loading}
        cart={cart}
        shippingMethods={shippingMethods}
        {...checkoutDetails}
      />
      <Popup isOpen={isModalOpen} bodyClassNames="sm:mx-5 sm:mx-unset max-w-[639px]">
        <div onClick={(e) => e.stopPropagation()} className="mb-[13px] flex justify-between pt-[24px] pr-[29px]">
          <div>
          </div>
          <div className="cursor-pointer" onClick={closeModal}>
            <Icon className="text-[#090909] w-[12px] h-[12px]" />
          </div>
        </div>
        <div className="px-[52px] flex flex-col items-center pb-[60px]">
          <Image src="/images/cph-logo.svg" className="w-[174px] h-[51px]" alt="Plumber's hub" />
          <Typography className="text-[27px] text-black text-center font-semibold pb-[23px] pt-[62px]">Oops!</Typography>
          <Typography className="text-[20px] text-black text-center max-w-[504px] font-normal pb-[56px]">Your payment was received, but something went wrong creating your order. Please reach out to our team for help.</Typography>
          <div className="flex sm:flex-row flex-col items-center gap-6 md:gap-[44px] w-full">
            <Button
              size="full"
              className="h-[60px] w-full !cursor-pointer text-[20px] font-medium !shadow-lg"
              variant="secondary"
              onClick={() => router.push('/contact-us')}

            >
              Contact Us
            </Button>
          </div>
        </div>
      </Popup>

    </>
  )
};

export default ReviewTastic;
